import { useCallback, useState } from "react";
import { ApolloError } from "@apollo/client";
import { message } from "antd";

import { CodeSectionReference } from "components/code-section-panel/code-section-panel";
import { CodeCitationValue } from "components/code-citation-picker/code-citation-picker";
import { CodeCitationOrigin, isBlockingPlanReviewFindingStatus } from "constants/field-values";
import { useUpdatePlanReviewFindingMutation } from "graphql/mutations/update-plan-review-finding.generated";
import { useRevertPlanReviewFindingMutation } from "graphql/mutations/revert-plan-review-finding.generated";
import { formatSectionNumberForCitation } from "utils/code-section-citation";

// Every screen that shows the findings or the outcome of a submittal. The job itself comes from a
// subscription, so its new outcome arrives without a refetch.
const FINDING_OVERRIDE_REFETCH_QUERIES = ["getPlanReviewFindings", "getFilteredPlanReviews"];

/** The values of a finding while the reviewer edits it. */
export interface PlanReviewFindingDraft {
	status: string;
	severity: string | null;
	summary: string;
	correctiveAction: string;
	codeCitations: CodeCitationValue[];
}

/** The finding fields that the edit form reads. */
export interface OverridableFinding {
	id: string;
	status?: string | null;
	severity?: string | null;
	summary?: string | null;
	corrective_action?: string | null;
}

const toCodeCitationValue = (reference: CodeSectionReference): CodeCitationValue => {
	const book = reference.code_section?.code_book;
	const sectionNumber = reference.code_section?.section_number ?? "";
	const builtLabel = book
		? `${book.abbreviation}${book.edition_year ? ` ${book.edition_year}` : ""} ${formatSectionNumberForCitation(sectionNumber)}`
		: sectionNumber;
	return {
		code_section_id: reference.code_section_id,
		citation_text: reference.citation_text ?? builtLabel,
		origin: CodeCitationOrigin.MANUAL,
		label: reference.citation_text ?? builtLabel
	};
};

const buildDraft = (finding: OverridableFinding, codeSections: CodeSectionReference[]): PlanReviewFindingDraft => ({
	status: finding.status ?? "",
	severity: finding.severity ?? null,
	summary: finding.summary ?? "",
	correctiveAction: finding.corrective_action && finding.corrective_action !== "null" ? finding.corrective_action : "",
	codeCitations: codeSections.map(toCodeCitationValue)
});

const sameCodeSectionIds = (first: string[], second: string[]): boolean =>
	first.length === second.length && first.every((id, index) => id === second[index]);

/**
 * Edit mode of a finding for the AHJ reviewer: the draft values, the reason, and the two
 * mutations (save changes and revert to Ollie's original). The backend validates every rule again.
 *
 * finding      — the finding shown in the modal.
 * codeSections — the code sections the finding shows now, used to start the draft.
 */
export default function usePlanReviewFindingOverride(
	finding: OverridableFinding | null,
	codeSections: CodeSectionReference[]
) {
	const [isEditing, setIsEditing] = useState(false);
	const [draft, setDraft] = useState<PlanReviewFindingDraft | null>(null);
	const [reason, setReason] = useState("");
	const [showReasonError, setShowReasonError] = useState(false);
	// Shown after a save, to ask the reviewer for AI accuracy feedback.
	const [showFeedbackPrompt, setShowFeedbackPrompt] = useState(false);

	const handleOnErrorUpdatePlanReviewFindingMutation = useCallback((error: ApolloError) => {
		console.error("Failed to update plan review finding:", error);
		message.error(error.graphQLErrors?.[0]?.message ?? "Couldn't save the finding. Please try again.");
	}, []);

	const handleOnCompleteUpdatePlanReviewFindingMutation = useCallback(() => {
		message.success("Finding updated.");
	}, []);

	const handleOnErrorRevertPlanReviewFindingMutation = useCallback((error: ApolloError) => {
		console.error("Failed to revert plan review finding:", error);
		message.error(error.graphQLErrors?.[0]?.message ?? "Couldn't revert the finding. Please try again.");
	}, []);

	const handleOnCompleteRevertPlanReviewFindingMutation = useCallback(() => {
		message.success("Finding reverted to Ollie’s original.");
	}, []);

	const [updatePlanReviewFindingMutation, { loading: saving }] = useUpdatePlanReviewFindingMutation({
		errorPolicy: "none",
		fetchPolicy: "network-only",
		notifyOnNetworkStatusChange: true,
		onError: handleOnErrorUpdatePlanReviewFindingMutation,
		onCompleted: handleOnCompleteUpdatePlanReviewFindingMutation,
		refetchQueries: FINDING_OVERRIDE_REFETCH_QUERIES,
		awaitRefetchQueries: true
	});

	const [revertPlanReviewFindingMutation, { loading: reverting }] = useRevertPlanReviewFindingMutation({
		errorPolicy: "none",
		fetchPolicy: "network-only",
		notifyOnNetworkStatusChange: true,
		onError: handleOnErrorRevertPlanReviewFindingMutation,
		onCompleted: handleOnCompleteRevertPlanReviewFindingMutation,
		refetchQueries: FINDING_OVERRIDE_REFETCH_QUERIES,
		awaitRefetchQueries: true
	});

	// A reason is required when a finding that blocks the approval stops blocking it.
	const isReasonRequired =
		!!finding &&
		!!draft &&
		isBlockingPlanReviewFindingStatus(finding.status) &&
		!isBlockingPlanReviewFindingStatus(draft.status);

	const startEditing = () => {
		if (!finding) return;
		setDraft(buildDraft(finding, codeSections));
		setReason("");
		setShowReasonError(false);
		setShowFeedbackPrompt(false);
		setIsEditing(true);
	};

	const cancelEditing = () => {
		setIsEditing(false);
		setShowReasonError(false);
	};

	const updateDraft = (changes: Partial<PlanReviewFindingDraft>) => {
		setDraft(current => (current ? { ...current, ...changes } : current));
		if (changes.status !== undefined) setShowReasonError(false);
	};

	const updateReason = (value: string) => {
		setReason(value);
		setShowReasonError(false);
	};

	const saveChanges = async () => {
		if (!finding || !draft) return;
		if (isReasonRequired && !reason.trim()) {
			setShowReasonError(true);
			return;
		}

		// The code sections are sent only when the reviewer changed the list, so a finding that
		// still uses its requirement's sections keeps them.
		const originalCodeSectionIds = codeSections.map(reference => reference.code_section_id);
		const draftCodeSectionIds = draft.codeCitations.map(citation => citation.code_section_id);
		const codeSectionsChanged = !sameCodeSectionIds(originalCodeSectionIds, draftCodeSectionIds);

		const { data } = await updatePlanReviewFindingMutation({
			variables: {
				input: {
					finding_id: finding.id,
					status: draft.status,
					severity: draft.severity,
					summary: draft.summary,
					corrective_action: draft.correctiveAction,
					code_section_ids: codeSectionsChanged ? draftCodeSectionIds : null,
					reason: reason.trim() || null
				}
			}
		});
		if (!data?.update_plan_review_finding) return;

		setIsEditing(false);
		setReason("");
		setShowFeedbackPrompt(true);
	};

	const revertToOllie = async () => {
		if (!finding) return;
		const { data } = await revertPlanReviewFindingMutation({ variables: { findingId: finding.id } });
		if (data?.revert_plan_review_finding) setShowFeedbackPrompt(false);
	};

	return {
		isEditing,
		draft,
		reason,
		isReasonRequired,
		showReasonError,
		showFeedbackPrompt,
		saving,
		reverting,
		startEditing,
		cancelEditing,
		updateDraft,
		updateReason,
		saveChanges,
		revertToOllie,
		hideFeedbackPrompt: () => setShowFeedbackPrompt(false)
	};
}
